import { ContentCard } from '../../components/ContentCard/ContentCard';
import { EcoLabels } from '../../components/EcoLabel/EcoLabels';
import { FavoriteButton } from '../../components/Favorites/FavoriteButton';
import { FlyToCartContainer } from '../../components/FlyToCart/FlyToCart';
import { Button } from '../../components/Form/Button';
import { QuantityInput } from '../../components/Form/QuantityInput';
import { Layout } from '../../components/Layout/Layout';
import { useConfig } from '../../config';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useCriteo } from '../../hooks/useCriteo';
import { useTranslation } from '../../i18n/i18n';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../state/cart';
import { ApiProductPage } from '../../types';
import { DeliveryDetails } from './DeliveryDetails';
import { ImageGallery } from './ImageGallery';
import { VariantSelector } from './VariantSelector';
import { PriceTag } from './PriceTag';
import './ProductPagePanes.css';
import { getSpecialPrice } from '../../components/PriceTagSimple/PriceTagSimple';
import { productBadge } from '../../components/ProductListing/ProductBox/ProductBox';
import { useLoop54Events } from '../../hooks/useLoop54Events';
import { useSession } from '../../state/session';
import { ProductContainer } from '../../state/product';
import { MetaContainer } from '../../state/meta';

export const ProductPageTopPane = ({
	productPageData,
	supercategoryId,
	cameFromSearchResults,
}: {
	productPageData: ApiProductPage;
	supercategoryId: number;
	cameFromSearchResults?: boolean;
}) => {
	const { setQuantity, currentCount } = useCart();
	const [quantityField, setQuantityField] = useState(productPageData?.bundleSize ?? 1);
	const t = useTranslation().product;
	const tRoutes = useTranslation().routes;
	const { flyToCart } = FlyToCartContainer.useContainer();
	const { specialPricesIndex } = ProductContainer.useContainer();
	const productNotInStock = productPageData.deliveryClass === 3 || productPageData.deliveryClass === 4;
	const { sendEvent, sendTorkEvent } = useAnalytics();
	const config = useConfig();
	const isCampaignProduct = productPageData.campaignPrice > 0;
	const { sendCriteoEvent } = useCriteo();
	const { store } = useConfig();
	const { sendLoop54Event } = useLoop54Events();
	const [searchTrackingSent, setSearchTrackingSent] = useState(false);
	const { isLoggedIn } = useSession();
	const { marketing } = MetaContainer.useContainer().trackingConsents;

	const handleAddToCart = (id: number) => {
		if (cameFromSearchResults && !searchTrackingSent) {
			if (typeof window !== 'undefined' && window.plausible) {
				window.plausible('successfulSearch');
			}
			setSearchTrackingSent(true);
		}
		setQuantity(id, currentCount(id) + quantityField);
		flyToCart();
		sendEvent({
			event: 'add_to_cart',
			ecommerce: {
				value: productPageData.price,
				currency: config.currencyCode,
				items: [
					{
						item_id: String(productPageData.id),
						item_name: productPageData.name,
						item_brand: productPageData.brand.name,
						price: productPageData.price,
						quantity: quantityField,
					},
				],
			},
		});

		if (store === 'kgno' && productPageData.brand.name.toLowerCase() === 'tork') {
			sendTorkEvent('AddToCart', {
				revenue: productPageData.price * quantityField,
			});
		}
		sendLoop54Event({ events: [{ type: 'addtocart', entity: { type: 'Product', id: productPageData.id + '' } }] });

		sendCriteoEvent({
			event: 'addToCart',
			item: [
				{
					id: productPageData.id + '',
					price: productPageData.price,
					quantity: quantityField,
				},
			],
		});

		if (typeof window.fbq !== 'undefined') {
			if (store === 'kgno') {
				if (productPageData.brand.name.toLowerCase() === 'tork' && marketing) {
					window.fbq('track', 'AddToCart', {
						value: productPageData.price * quantityField,
						currency: 'NOK',
					});
				}
			} else {
				window.fbq('track', 'AddToCart', {
					content_type: 'product',
					content_ids: [id + ''],
					content_name: productPageData.name,
					value: quantityField * productPageData.price,
					currency: config.currencyCode,
				});
			}
		}
	};

	const { id, name, ecoLabels, brand, bundleSize, campaignPrice } = productPageData;
	const hasSpecialPrice = !!getSpecialPrice(id);
	const specialPriceProduct = specialPricesIndex ? specialPricesIndex[productPageData.id] : undefined;

	return (
		<div className="product-info-top-pane">
			<Layout type="two-pane">
				<ContentCard className="left-pane">
					<div className={isCampaignProduct ? 'campaign-product' : ''}>
						{productBadge(
							hasSpecialPrice,
							campaignPrice > 0,
							false,
							specialPriceProduct,
							`category-color-${supercategoryId}`
						)}
						<EcoLabels
							ecoLabels={ecoLabels}
							isLoggedIn={isLoggedIn}
							size={22}
							hasRibbon={campaignPrice > 0 || hasSpecialPrice}
						/>
					</div>
					<div />
					<ImageGallery images={productPageData.images} />
				</ContentCard>
				<ContentCard
					className={`right-pane category-color-${supercategoryId}-light ${
						productNotInStock ? 'product-not-in-stock-color-light' : ''
					} ${productPageData.variants ? 'has-variants' : ''}`}
				>
					<Layout type="two-pane">
						<div className="brand-icon">
							{brand.slug && brand.slug != '' ? (
								<Link to={`${tRoutes.brands}/${brand.slug}`}>{brand.name}</Link>
							) : (
								brand.name
							)}
							{/* TODO: brand icon goes here */}
						</div>

						<div>
							<FavoriteButton id={id} />
						</div>
					</Layout>
					<h1>{name}</h1>
					{productPageData.variants && (
						<VariantSelector
							variants={productPageData.variants}
							groupProducts={productPageData.groupProducts}
							currentId={id}
						/>
					)}
					<DeliveryDetails product={productPageData} />
					<PriceTag product={productPageData} />
					<div className="buy-button-area">
						<QuantityInput
							defaultValue={quantityField}
							onBlur={(value) => setQuantityField(value)}
							step={bundleSize}
						/>
						<Button
							className={`product-page ${productNotInStock ? 'product-not-in-stock-color' : ''} plausible-event-name=Add+to+Cart`}
							onClick={(_e) => handleAddToCart(id)}
							disabled={productPageData.deliveryClass === 6}
						>
							{productNotInStock ? t.placeOrder : t.buyNow}
						</Button>
					</div>
				</ContentCard>
			</Layout>
		</div>
	);
};
